import {
  ColDef,
  GridApi,
  GridReadyEvent,
  RowClickedEvent,
} from 'ag-grid-community';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectFinancialsTableRowData } from 'api/project';
import { Button } from 'components/atoms/button';
import { Grid } from 'components/atoms/grid';
import { TableSkeleton } from 'components/atoms/table';
import { strigifyArray } from 'utils/project-financials';
import {
  ColumnHeaderRenderer,
  GroupCellRenderer,
  GroupHeaderRenderer,
} from './table-renderers';

const GridContainer = styled.div`
  width: 100%;
  height: 400px;
`;

const StyledGrid = styled(Grid)`
  --ag-header-column-resize-handle-display: none;

  .ag-header {
    height: 64px;

    .ag-header-row {
      .periodicity-header {
        background-color: var(--coolest-gray-40);
      }

      .actuality-header {
        background-color: var(--coolest-gray-30);
      }

      .field-header {
        background-color: var(--coolest-gray-20);
      }

      .ag-header-cell {
        border-top: none !important;
      }

      .ag-header-group-cell {
        border-top: none !important;

        > .ag-react-container {
          justify-content: center;
        }

        &.ag-header-group-cell-no-group {
          background-color: var(--white-100);
        }
      }
    }

    .ag-header-cell::after,
    .ag-header-group-cell::after {
      top: 0;
      height: 100%;
      background-color: var(--gray-10);
    }
  }

  .last-cell-column-in-group {
    border-right: 1px solid var(--gray-10) !important;
  }

  .ag-cell-wrapper {
    flex: 1;

    .ag-group-value {
      flex: 1;
    }
  }

  /**
   * Temporary hack to hide the duplicate component rendered
   * Ref: https://github.com/ag-grid/ag-grid/issues/3731
   */
  .ag-react-container > :not(:first-child) {
    display: none !important;
  }

  .ag-row {
    border-color: var(--coolest-gray-20) !important;
    background-color: var(--white-100);

    &.bold-text {
      font-weight: var(--heading-8-weight);
    }

    &.gray-bg {
      background-color: var(--coolest-gray-10);
    }
  }
  .ag-row-focus {
    background: none;
  }
  .ag-row-hover {
    background: var(--ag-row-hover-color);
  }
`;

interface Props {
  isTableLoading: boolean;
  rowData: ProjectFinancialsTableRowData[];
  columnDefs: ColDef[];
  autoGroupColumnDef: ColDef;
}

export const FinancialsTable: FC<Props> = ({
  isTableLoading,
  rowData,
  columnDefs,
  autoGroupColumnDef,
}) => {
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const onRowClicked = useCallback(({ node }: RowClickedEvent) => {
    node.setExpanded(!node.expanded);
  }, []);

  const getDataPath = useMemo(() => {
    return (data) => {
      return data.hierarchy;
    };
  }, []);

  const getRowClass = (params: any): string => {
    const { data } = params;
    const row = data as ProjectFinancialsTableRowData;
    const { hierarchy } = row;

    return `${hierarchy.length === 1 ? 'bold-text' : ''}${
      hierarchy.length > 2 ? ' gray-bg' : ''
    }`;
  };

  const getRowNodeId = (params: any): string => {
    const row = params as ProjectFinancialsTableRowData;
    const { sourceType, sourceName, hierarchy } = row;

    return `${strigifyArray(sourceType)}-${strigifyArray(
      sourceName,
    )}-${hierarchy.join('-')}`;
  };

  const exportAsCsv = useCallback(() => {
    if (gridApi == null) {
      return;
    }

    gridApi?.exportDataAsCsv();
  }, [gridApi]);

  const defaultColDef: ColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 160,
      filter: false,
      sortable: false,
      resizable: false,
      wrapText: false,
      editable: false,
      rowDrag: false,
      suppressMenu: true,
      suppressMovable: true,
      headerComponent: 'columnHeaderRenderer',
      suppressKeyboardEvent: () => true,
      suppressHeaderKeyboardEvent: () => true,
    };
  }, []);

  return isTableLoading ? (
    <TableSkeleton columnCount={4} rowCount={7} size="large" />
  ) : (
    <>
      <Button onClick={exportAsCsv} hidden>
        <FormattedMessage id="export" />
      </Button>
      <GridContainer className="ag-theme-balham">
        <StyledGrid
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          size="large"
          getDataPath={getDataPath}
          treeData
          animateRows
          autoSetHeaderHeight
          suppressContextMenu
          suppressRowClickSelection
          suppressCellSelection
          suppressRowHoverHighlight
          frameworkComponents={{
            columnHeaderRenderer: ColumnHeaderRenderer,
            groupHeaderRenderer: GroupHeaderRenderer,
            groupCellRenderer: GroupCellRenderer,
          }}
          getRowClass={getRowClass}
          getRowNodeId={getRowNodeId}
          onRowClicked={onRowClicked}
          onGridReady={onGridReady}
        />
      </GridContainer>
    </>
  );
};
